import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  auth as backendAuth,
  getAuthThrottleState,
} from "../lib/backend/auth";
import { useAuth } from "../hooks/useAuth";
import { registerProvider } from "../lib/providers";
import Logo from "../components/Logo";
import SEOHead from "../components/SEOHead";
import "./Login.css";

// Per ADR #0003: new accounts are magic-link-only. Password is a fallback
// for existing accounts only. We model this with an explicit `mode`:
//   "magic"    → email-first, OTP confirms (works for new + existing emails)
//   "password" → legacy password sign-in (existing users only)
//
// In "magic" mode we kick the buyer into the SDK's existing OTP/verifyEmail
// flow by calling signUp with a strong throwaway password. The backend
// (Insforge) responds with `requireEmailVerification` for fresh emails
// and surfaces "user already exists" for known ones — the latter triggers
// a fallback to `resendVerificationEmail`, which re-uses the same OTP
// channel for a magic-link-style sign-in. This works with the SDK we have
// today without requiring a new edge function.
function randomThrowawayPassword() {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    // Base64url so the result is URL/JSON safe and well above the SDK's
    // 6-char minimum.
    return btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }
  return `pkr-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function emailLocalPart(email) {
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

export default function Login() {
  const { t } = useTranslation("login");
  const { t: tProvider } = useTranslation("provider");
  // Default mode is magic-link per issue #199. The password tab is opt-in.
  const [mode, setMode] = useState("magic"); // 'magic' | 'password'
  // Account-type picker (issue #201). `null` => the picker is shown;
  // otherwise the email form for the chosen role is shown. The Provider
  // path also asks for company name + contact email before signing up so
  // we have the data to insert the `providers` row right after verification.
  const [accountType, setAccountType] = useState(null); // 'buyer' | 'organizer' | 'provider' | null
  const [providerName, setProviderName] = useState("");
  const [providerContactEmail, setProviderContactEmail] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // ── Failed-attempts counter / countdown (issue #68) ──
  const [lockoutUntil, setLockoutUntil] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Email verification state (used by both magic-link and password+verify).
  const [verifyStep, setVerifyStep] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(false);

  const { signIn, signInWithOAuth, checkUser } = useAuth();
  const navigate = useNavigate();

  // After a successful Provider sign-up + email verification we land on
  // /provider/dashboard. Other roles go to /dashboard.
  const postAuthRoute = accountType === "provider" ? "/provider/dashboard" : "/dashboard";

  // ── Lockout countdown tick (issue #68) ──
  useEffect(() => {
    if (!lockoutUntil) {
      setSecondsLeft(0);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        setLockoutUntil(0);
        setError(null);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockoutUntil]);

  // ── Surface a pre-existing lockout when the user types an email ──
  useEffect(() => {
    if (mode !== "password") return;
    const state = getAuthThrottleState(email);
    if (state.blocked) {
      setLockoutUntil(Date.now() + state.retryAfterMs);
    }
  }, [email, mode]);

  /* ── Google OAuth ── */
  const handleGoogleLogin = async () => {
    setError(null);
    try {
      await signInWithOAuth("google");
    } catch (oauthError) {
      setError(oauthError.message || t("errorGoogle"));
    }
  };

  /* ── Magic-link / passwordless start ── */
  const handleMagicSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (accountType === "provider") {
      if (!providerName.trim()) {
        setError(tProvider("register.errors.nameRequired"));
        return;
      }
      if (!providerContactEmail.trim()) {
        setError(tProvider("register.errors.emailRequired"));
        return;
      }
    }
    setLoading(true);
    const trimmed = email.trim();
    try {
      // Try signUp first. For a fresh email the backend sends an OTP and
      // returns `requireEmailVerification: true`. For an existing email the
      // SDK errors — we then nudge a verification code via
      // `resendVerificationEmail` so the same OTP UI handles both cases.
      const { data, error: signUpError } = await backendAuth.signUp({
        email: trimmed,
        password: randomThrowawayPassword(),
        name: emailLocalPart(trimmed),
      });

      if (signUpError) {
        // Common: "user already exists". Re-send the verification code so
        // the user receives a one-tap code to complete sign-in.
        const { error: resendError } =
          await backendAuth.resendVerificationEmail({ email: trimmed });
        if (resendError) {
          throw resendError;
        }
        setVerifyStep(true);
        return;
      }

      if (data?.requireEmailVerification) {
        setVerifyStep(true);
        return;
      }

      // No verification gate (unusual for fresh signups): refresh session
      // and route to dashboard.
      await checkUser();
      await maybeCreateProviderRow();
      navigate(postAuthRoute);
    } catch (err) {
      setError(err.message || t("errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  // For Provider sign-ups we insert the `providers` row immediately after
  // the user's email is verified. RLS on `providers` requires
  // `created_by_user_id = auth.uid()`, so we must run this after the SDK
  // has materialised the session.
  const maybeCreateProviderRow = async () => {
    if (accountType !== "provider") return;
    const { data: userResult } = await backendAuth.getCurrentUser();
    const uid = userResult?.user?.id;
    if (!uid) return;
    await registerProvider({
      userId: uid,
      name: providerName.trim(),
      contactEmail: providerContactEmail.trim() || email.trim(),
    });
  };

  /* ── Password sign-in (fallback for existing users) ── */
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signIn(email, password);
      setLockoutUntil(0);
      navigate(postAuthRoute);
    } catch (err) {
      if (err && err.code === "auth/rate-limited") {
        setLockoutUntil(Date.now() + (err.retryAfterMs || 0));
      } else {
        setError(err.message || t("errorGeneric"));
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Verify email code ── */
  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: verifyError } = await backendAuth.verifyEmail({
        email: email.trim(),
        otp: otp.trim(),
      });

      if (verifyError) throw verifyError;

      // verifyEmail auto-saves the session — user is now signed in
      await checkUser();
      await maybeCreateProviderRow();
      navigate(postAuthRoute);
    } catch (err) {
      setError(err.message || t("errorInvalidCode"));
    } finally {
      setLoading(false);
    }
  };

  /* ── Resend verification code ── */
  const handleResend = async () => {
    if (resendCooldown) return;
    setError(null);
    setResendCooldown(true);
    try {
      await backendAuth.resendVerificationEmail({ email: email.trim() });
    } catch (err) {
      setError(err.message || t("errorResend"));
    }
    setTimeout(() => setResendCooldown(false), 60000);
  };

  // ── Verification step UI ──
  if (verifyStep) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-brand">
            <Logo size={28} className="login-brand-icon" />
            <span className="login-brand-text">pikra</span>
          </div>

          <p style={{ textAlign: "center", color: "#b0b0c0", marginBottom: 8 }}>
            {t("verifyMessage")}
          </p>
          <p style={{ textAlign: "center", color: "#fff", fontWeight: 600, marginBottom: 8 }}>
            {email}
          </p>
          {/* The backend answers `resendVerificationEmail` with the same
              neutral success for unknown emails (anti-enumeration), so an
              address that was never provisioned lands here too and would
              otherwise wait for a code that is never sent. */}
          <p
            style={{ textAlign: "center", color: "#8b8ba7", fontSize: "0.85rem", marginBottom: 24 }}
            data-testid="login-provisioned-only"
          >
            {t("verifyProvisionedOnly")}
          </p>

          <form className="login-form" onSubmit={handleVerify}>
            <div className="login-field">
              <label htmlFor="otp">{t("verifyLabel")}</label>
              <input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                required
                maxLength={6}
                autoComplete="one-time-code"
                style={{ textAlign: "center", fontSize: "1.5rem", letterSpacing: "0.5em" }}
                data-testid="login-otp-input"
              />
            </div>

            {error && <p className="login-error">{error}</p>}

            <button
              type="submit"
              className="login-submit"
              disabled={loading || otp.length < 6}
              data-testid="login-verify-submit"
            >
              {loading ? t("verifying") : t("verify")}
            </button>
          </form>

          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown}
            style={{
              background: "none",
              border: "none",
              color: resendCooldown ? "#55556a" : "#8b8ba7",
              cursor: resendCooldown ? "default" : "pointer",
              marginTop: 16,
              fontSize: "0.9rem",
              width: "100%",
              textAlign: "center",
            }}
          >
            {resendCooldown ? t("resendCooldown") : t("resend")}
          </button>
        </div>
      </div>
    );
  }

  // ── Account-type picker (issue #201) ──
  // The picker is plain disclosure: three buttons that record the user's
  // chosen role before they enter their email. The buyer/organizer paths
  // share the existing magic-link flow; provider also collects company
  // info up-front so we can insert the `providers` row right after the
  // session is materialised.
  if (accountType === null) {
    return (
      <div className="login-page">
        <SEOHead title={t("seoLogin")} description={t("seoDescription")} />
        <div className="login-card">
          <h1 className="login-brand">pikra</h1>
          <div className="login-role-picker" data-testid="login-role-picker">
            <button
              type="button"
              className="login-role-btn"
              onClick={() => setAccountType("buyer")}
              data-testid="login-role-buyer"
            >
              {t("roleBuyer")}
            </button>
            <button
              type="button"
              className="login-role-btn"
              onClick={() => setAccountType("organizer")}
              data-testid="login-role-organizer"
            >
              {t("roleOrganizer")}
            </button>
            <button
              type="button"
              className="login-role-btn"
              onClick={() => setAccountType("provider")}
              data-testid="login-role-provider"
            >
              {t("roleProvider")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <SEOHead
        title={t("seoLogin")}
        description={t("seoDescription")}
      />
      <div className="login-card">
        <h1 className="login-brand">
          pikra
        </h1>

        <button
          type="button"
          className="login-mode-toggle"
          onClick={() => {
            setAccountType(null);
            setError(null);
          }}
          data-testid="login-role-back"
        >
          {t("roleBack")}
        </button>

        {/* ── Google OAuth button ── */}
        <button
          type="button"
          className="login-google-btn"
          onClick={handleGoogleLogin}
        >
          <svg
            className="login-google-icon"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            aria-hidden="true"
          >
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          {t("continueGoogle")}
        </button>

        {/* ── Divider ── */}
        <div className="login-divider">
          <span className="login-divider-line" />
          <span className="login-divider-text">{t("or")}</span>
          <span className="login-divider-line" />
        </div>

        {mode === "magic" ? (
          <form
            className="login-form"
            onSubmit={handleMagicSubmit}
            data-testid="login-magic-form"
          >
            {accountType === "provider" && (
              <>
                <div className="login-field">
                  <label htmlFor="provider-name">
                    {tProvider("register.nameLabel")}
                  </label>
                  <input
                    id="provider-name"
                    type="text"
                    value={providerName}
                    onChange={(e) => setProviderName(e.target.value)}
                    placeholder={tProvider("register.namePlaceholder")}
                    required
                    maxLength={100}
                    data-testid="login-provider-name"
                  />
                </div>
                <div className="login-field">
                  <label htmlFor="provider-contact-email">
                    {tProvider("register.contactEmailLabel")}
                  </label>
                  <input
                    id="provider-contact-email"
                    type="email"
                    value={providerContactEmail}
                    onChange={(e) => setProviderContactEmail(e.target.value)}
                    placeholder={tProvider("register.contactEmailPlaceholder")}
                    required
                    data-testid="login-provider-contact-email"
                  />
                </div>
              </>
            )}
            <div className="login-field">
              <label htmlFor="email">{t("email")}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("emailPlaceholder")}
                required
                autoComplete="email"
                data-testid="login-magic-email"
              />
            </div>

            {error && <p className="login-error">{error}</p>}

            <button
              type="submit"
              className="login-submit"
              disabled={loading || !email.trim()}
              data-testid="login-magic-submit"
            >
              {loading
                ? t("states.loading", { ns: "common" })
                : t("submitMagicLink")}
            </button>

            <p className="login-magic-help">
              {t("magicHelp")}
            </p>

            <button
              type="button"
              className="login-mode-toggle"
              onClick={() => {
                setMode("password");
                setError(null);
              }}
              data-testid="login-switch-to-password"
            >
              {t("usePassword")}
            </button>
          </form>
        ) : (
          <form
            className="login-form"
            onSubmit={handlePasswordSubmit}
            data-testid="login-password-form"
          >
            <div className="login-field">
              <label htmlFor="email">{t("email")}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("emailPlaceholder")}
                required
                autoComplete="email"
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">{t("password")}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("passwordPlaceholder")}
                required
                minLength={6}
                autoComplete="current-password"
              />
            </div>

            {lockoutUntil > 0 && secondsLeft > 0 ? (
              <p className="login-error" data-testid="login-rate-limited">
                {t("errorRateLimited", { seconds: secondsLeft })}
              </p>
            ) : (
              error && <p className="login-error">{error}</p>
            )}

            <button
              type="submit"
              className="login-submit"
              disabled={loading || (lockoutUntil > 0 && secondsLeft > 0)}
              data-testid="login-password-submit"
            >
              {loading
                ? t("states.loading", { ns: "common" })
                : t("submitLogin")}
            </button>

            <button
              type="button"
              className="login-mode-toggle"
              onClick={() => {
                setMode("magic");
                setError(null);
              }}
              data-testid="login-switch-to-magic"
            >
              {t("useMagicLink")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
